import { ModuleDocContent } from "./docsData";

export const enterpriseModuleDocs: Record<string, ModuleDocContent> = {
  "sso": {
    title: "SSO Configuration",
    subtitle: "Single Sign-On Setup",
    color: "slate",
    badge: "Enterprise",
    description:
      "Configure OAuth/OpenID Connect providers for single sign-on. Add providers from 8 pre-configured templates, manage client credentials, attribute mapping, and test connections before enabling.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>SSO Configuration</strong> lets you manage OAuth and OpenID Connect identity providers so users can sign in with their existing corporate credentials. Add providers from pre-configured templates, configure client credentials, map attributes, and test connections.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/6a4c0414-f2ef-4a5e-86b5-e9faa598ed6c.png" alt="SSO Configuration" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: SSO configuration page</p></div>

      <section>
        <h2>Provider List</h2>
        <p>The main page displays all configured identity providers in a table with bulk operations.</p>

        <h3>Table Columns</h3>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Checkbox</strong></td><td>Multi-select for bulk enable/disable operations.</td></tr>
            <tr><td><strong>Provider</strong></td><td>Display name, provider name, and logo image.</td></tr>
            <tr><td><strong>Client ID</strong></td><td>First 20 characters of the client ID (truncated).</td></tr>
            <tr><td><strong>Status</strong></td><td>Enabled (green) or Disabled (gray) badge.</td></tr>
            <tr><td><strong>Priority</strong></td><td>Numeric ordering value (lower numbers appear first).</td></tr>
            <tr><td><strong>Sign Up</strong></td><td>Checkmark or cross indicating whether new user sign-up is allowed.</td></tr>
            <tr><td><strong>Type</strong></td><td>Provider type badge.</td></tr>
            <tr><td><strong>Actions</strong></td><td>Test, Sync Well-Known, Toggle Enable/Disable, Edit, Delete.</td></tr>
          </tbody>
        </table>

        <h3>Filters</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Search by display name or provider name.</td></tr>
            <tr><td><strong>Status</strong></td><td>All Status, Enabled, Disabled.</td></tr>
          </tbody>
        </table>

        <h3>Actions Per Provider</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Test Configuration</strong></td><td>Validate the provider connection. Returns success or detailed error messages.</td></tr>
            <tr><td><strong>Sync Well-Known</strong></td><td>Fetch and update OIDC endpoints from the provider's well-known URL.</td></tr>
            <tr><td><strong>Toggle Enable/Disable</strong></td><td>Enable or disable the provider for login.</td></tr>
            <tr><td><strong>Edit</strong></td><td>Open the provider configuration form.</td></tr>
            <tr><td><strong>Delete</strong></td><td>Remove the provider (with confirmation).</td></tr>
          </tbody>
        </table>
        <p>Bulk actions: Select multiple providers and enable or disable them all at once.</p>
        <p>Footer shows: total provider count, enabled count, and disabled count.</p>
      </section>

      <section>
        <h2>Add / Edit Provider</h2>

        <h3>Quick Setup Templates</h3>
        <p>When creating a new provider, choose from 8 pre-configured templates:</p>
        <table>
          <thead>
            <tr>
              <th>Template</th>
              <th>Protocol</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Google</strong></td><td>OpenID Connect</td></tr>
            <tr><td><strong>Microsoft Azure AD</strong></td><td>OpenID Connect</td></tr>
            <tr><td><strong>Okta</strong></td><td>OpenID Connect</td></tr>
            <tr><td><strong>Auth0</strong></td><td>OpenID Connect</td></tr>
            <tr><td><strong>Keycloak</strong></td><td>OpenID Connect</td></tr>
            <tr><td><strong>GitHub</strong></td><td>OAuth 2.0</td></tr>
            <tr><td><strong>LinkedIn</strong></td><td>OAuth 2.0</td></tr>
            <tr><td><strong>Facebook</strong></td><td>OAuth 2.0</td></tr>
          </tbody>
        </table>
        <p>Selecting a template pre-fills fields and shows provider-specific setup instructions.</p>

        <h3>Basic Configuration</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Provider Name</strong></td><td>Unique identifier (lowercase alphanumeric with hyphens/underscores). Immutable after creation.</td></tr>
            <tr><td><strong>Display Name</strong></td><td>Shown on the login screen (e.g., "Sign in with Google").</td></tr>
            <tr><td><strong>Logo URL</strong></td><td>Optional provider logo image URL.</td></tr>
            <tr><td><strong>Priority</strong></td><td>Ordering number (lower = appears first).</td></tr>
            <tr><td><strong>Enabled</strong></td><td>Toggle to activate the provider.</td></tr>
            <tr><td><strong>Allow Sign Up</strong></td><td>Allow new user accounts to be created on first SSO login.</td></tr>
            <tr><td><strong>Auto-link Existing Accounts</strong></td><td>Automatically link SSO login to existing accounts with matching email.</td></tr>
          </tbody>
        </table>

        <h3>OAuth Configuration</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>OpenID Well-Known URL</strong></td><td>Required for OIDC providers. Use the inline Sync button to validate and auto-discover endpoints.</td></tr>
            <tr><td><strong>Client ID</strong></td><td>OAuth client identifier from your provider.</td></tr>
            <tr><td><strong>Client Secret</strong></td><td>Required when creating. In edit mode, leave blank to keep the existing secret (masked for security).</td></tr>
            <tr><td><strong>Redirect URI</strong></td><td>Auto-generated callback URL (read-only). Copy this and add it to your provider's allowed redirect URIs.</td></tr>
            <tr><td><strong>Scopes</strong></td><td>Space-separated OAuth scopes (e.g., "openid profile email").</td></tr>
            <tr><td><strong>Response Type</strong></td><td>Authorization Code Flow, Implicit Flow, id_token, or Hybrid Flow.</td></tr>
          </tbody>
        </table>

        <h3>Advanced Configuration</h3>
        <p>Optional JSON textarea for provider-specific parameters (e.g., <code>{"prompt": "consent", "access_type": "offline"}</code>).</p>

        <h3>Attribute Mapping</h3>
        <p>Map provider fields to user attributes. Defaults work for most OIDC providers:</p>
        <table>
          <thead>
            <tr>
              <th>Mapping</th>
              <th>Default Value</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Email Field</strong></td><td>email</td></tr>
            <tr><td><strong>First Name Field</strong></td><td>given_name</td></tr>
            <tr><td><strong>Last Name Field</strong></td><td>family_name</td></tr>
            <tr><td><strong>Picture Field</strong></td><td>picture</td></tr>
            <tr><td><strong>Subject/User ID Field</strong></td><td>sub</td></tr>
          </tbody>
        </table>

        <h3>Provider Metadata</h3>
        <p>Optional fields: Provider Type, Company, Support URL, Domain, and Description.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Corporate SSO with Azure AD</div>
            <div class="sz-use-case-flow">
              Click Add Provider, select the Microsoft Azure AD template. Enter your Client ID, Client Secret, and Well-Known URL. Click Sync to auto-discover endpoints. Copy the Redirect URI into Azure. Test the configuration, then enable.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Multiple providers for different teams</div>
            <div class="sz-use-case-flow">
              Configure separate providers for different organizations (e.g., Google for one team, Okta for another). Set priority values to control ordering on the login screen. Use bulk enable/disable for maintenance windows.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Always use the <strong>Test Configuration</strong> button before enabling a provider. Use <strong>Sync Well-Known</strong> to auto-discover OIDC endpoints instead of entering them manually.</p>
      </div>

    </div>`,
  },

  "scim": {
    title: "SCIM Configuration",
    subtitle: "Automated User Provisioning",
    color: "slate",
    badge: "Enterprise",
    description:
      "Configure SCIM providers to automatically sync users, groups, and roles from your identity provider. Supports multiple provider types, dry runs, automatic scheduling, and detailed sync history.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>SCIM Configuration</strong> automates user provisioning by syncing users, groups, and roles from your identity provider. Configure multiple SCIM providers, run dry syncs to preview changes, set up automatic schedules, and track every sync operation in the history log.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b975a26f-d1ed-4276-a824-8435af3fb7d8.png" alt="SCIM Configuration" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: SCIM provisioning settings</p></div>

      <section>
        <h2>Dashboard Stats</h2>
        <p>Five metrics cards at the top of the page:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Providers</strong></td><td>Number of configured SCIM providers (with active count).</td></tr>
            <tr><td><strong>Connected</strong></td><td>Successfully connected providers with percentage.</td></tr>
            <tr><td><strong>Total Syncs</strong></td><td>Total sync operations performed (with 24-hour count).</td></tr>
            <tr><td><strong>Success Rate</strong></td><td>Overall sync success percentage.</td></tr>
            <tr><td><strong>Failed Syncs</strong></td><td>Number of failed syncs with failure rate.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Tabs</h2>
        <p>The page has two tabs:</p>

        <h3>Providers Tab</h3>
        <p>Displays configured SCIM providers as cards. Each card shows:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Provider Name</strong></td><td>User-defined name for the provider.</td></tr>
            <tr><td><strong>Provider Type</strong></td><td>SCIM 2.0, Skillmine Auth, or Spotlight.</td></tr>
            <tr><td><strong>Status Badges</strong></td><td>Connection status, sync status, active status, and auto-sync schedule.</td></tr>
            <tr><td><strong>Last Sync</strong></td><td>Timestamp with relative time display.</td></tr>
          </tbody>
        </table>

        <p>Actions per provider:</p>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Edit</strong></td><td>Modify provider configuration.</td></tr>
            <tr><td><strong>Delete</strong></td><td>Remove the provider.</td></tr>
            <tr><td><strong>Dry Run</strong></td><td>Test sync without making actual changes &mdash; preview what would be created, updated, or skipped.</td></tr>
            <tr><td><strong>Sync Now</strong></td><td>Execute a real sync operation (with confirmation dialog).</td></tr>
          </tbody>
        </table>

        <p>Filters: Search by provider name, filter by provider type (All/SCIM 2.0/Skillmine Auth/Spotlight). Paginated with 10, 20, or 50 items per page.</p>

        <h3>Sync History Tab</h3>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Provider</strong></td><td>Provider name.</td></tr>
            <tr><td><strong>Sync Type</strong></td><td>Full, groups, or users. Shows "Dry Run" badge if applicable.</td></tr>
            <tr><td><strong>Status</strong></td><td>Completed (green), Failed (red), In Progress (blue), Partial (yellow).</td></tr>
            <tr><td><strong>Users</strong></td><td>Total users with created/updated/failed counts.</td></tr>
            <tr><td><strong>Groups</strong></td><td>Total groups with created/updated/failed counts.</td></tr>
            <tr><td><strong>Duration</strong></td><td>Time taken in seconds.</td></tr>
            <tr><td><strong>Started At</strong></td><td>Full timestamp.</td></tr>
            <tr><td><strong>Actions</strong></td><td>View Details link &mdash; opens sync detail modal with tabs for Users, Groups, Roles, and Mappings.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Add / Edit Provider</h2>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Basic Information</strong></td>
              <td>Provider Name (required, 3+ chars), Provider Type (SCIM 2.0 / Skillmine Auth / Spotlight).</td>
            </tr>
            <tr>
              <td><strong>Connection Settings</strong></td>
              <td>Base URL (HTTPS), Token Endpoint (HTTPS), Client ID, Client Secret (optional on edit).</td>
            </tr>
            <tr>
              <td><strong>Sync Configuration</strong></td>
              <td>Resource types to sync (Users, Groups, Roles checkboxes), Auto-assign Skillzen Learner Group toggle, Batch Size (1&ndash;1000), Conflict Resolution strategy.</td>
            </tr>
            <tr>
              <td><strong>Auto-Sync Schedule</strong></td>
              <td>Enable toggle, Frequency presets (30min, 1h, 6h, 12h, daily, weekly, monthly, custom cron), Next Scheduled Sync display.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Active Configuration toggle.</td>
            </tr>
          </tbody>
        </table>

        <h3>Conflict Resolution Strategies</h3>
        <table>
          <thead>
            <tr>
              <th>Strategy</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>SKIP</strong></td><td>Keep existing data unchanged.</td></tr>
            <tr><td><strong>UPDATE</strong></td><td>Replace all fields with provider data.</td></tr>
            <tr><td><strong>UPDATE + Additive Roles</strong></td><td>Replace data but accumulate roles.</td></tr>
            <tr><td><strong>UPDATE + Preserve Local Groups</strong></td><td>Protect skillzen_* groups from overwrite.</td></tr>
            <tr><td><strong>MERGE</strong></td><td>Fill only empty fields, keep existing values.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Sync Details Modal</h2>
        <p>Viewing sync details opens a modal with four tabs:</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Shows</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Users</strong></td><td>Created, updated, merged, and skipped users with field-level change tracking.</td></tr>
            <tr><td><strong>Groups</strong></td><td>Created, updated, merged, and skipped groups.</td></tr>
            <tr><td><strong>Roles</strong></td><td>Role changes and assignments.</td></tr>
            <tr><td><strong>Mappings</strong></td><td>Group-role mapping changes.</td></tr>
          </tbody>
        </table>
        <p>Each tab includes search and shows performance metrics: total duration, fetch/transform/persist duration, and items per second.</p>
      </section>

      <section>
        <h2>Sync History Archiving (Hot / Cold Storage)</h2>
        <p>Every sync writes a history record, and the change detail behind each one can be large, so over time the sync-history collection bloats the database. To keep it lean, older sync logs are moved out of MongoDB into compressed files in the tenant's own S3 bucket. Records aged past the retention window (30 days) are archived one complete month at a time — the history stays complete, it simply no longer weighs down the live collection.</p>

        <div class="sz-callout sz-callout-info">
          <p>Archival runs automatically once a day at <strong>2:00 AM UTC</strong>, per tenant. Each old month is written to S3 as a small searchable <strong>index file</strong> (provider, sync type, status, counts, timing) plus a full <strong>data file</strong> that carries the heavy detail — the per-record field-level changes, sync errors, and conflicts — then catalogued with one manifest row and removed from the live collection. Files are uploaded before anything is deleted, so no history is ever at risk, and the store is roughly 10× smaller once compressed.</p>
        </div>

        <h3>Include Archived toggle</h3>
        <p>By default the Sync History tab lists only recent (hot) syncs so it loads instantly. Turn on <strong>Include Archived</strong> — a Datadog-style "Historical Index" opt-in — to also search archived months; the same filters and sorting apply, and only the small index files are read to build the merged list. The full change detail (the four-tab Sync Details modal — Users, Groups, Roles, Mappings) is fetched from the month's data file only when you open one archived sync's details.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/0487846a-99f0-439d-b70f-524d223c0fa8.png" alt="SCIM Sync History — default view, recent records only" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: SCIM Sync History — default view (recent hot records only, archived months excluded)</p></div>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/5873026d-ef4f-468b-8adf-859cad5cb024.png" alt="SCIM Sync History — Include Archived enabled" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: SCIM Sync History with <strong>Include Archived</strong> on — older archived months merged into the same history</p></div>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Preview before syncing</div>
            <div class="sz-use-case-flow">
              Click Dry Run on a provider to preview what changes would happen without actually modifying any data. Review the results in Sync History, then click Sync Now when ready.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Automated daily user sync</div>
            <div class="sz-use-case-flow">
              Configure a SCIM 2.0 provider with your IdP credentials. Enable auto-sync with "Daily at 2 AM UTC" frequency. Set conflict resolution to UPDATE + Preserve Local Groups so local group assignments are protected.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Always run a <strong>Dry Run</strong> before your first sync to preview changes. Use the <strong>MERGE</strong> conflict resolution strategy when you want to fill gaps without overwriting manually set values.</p>
      </div>

    </div>`,
  },

  "credit-plans": {
    title: "Credit Plans",
    subtitle: "Credit Allotment & Validity",
    color: "slate",
    badge: "Enterprise",
    description:
      "Define reusable credit plans — the credit allotment and validity period granted for AI generation — with auto-renew and rollover behavior. Create, search, edit, and delete plans from a card-based list. Management-tenant only.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Credit Plans</strong> let you define how many credits a plan grants and how long they stay valid, plus whether credits auto-renew and whether unused credits roll over. Each plan is a reusable template identified by a stable <strong>plan key</strong>.</p>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Management-tenant only</div>
        <p>Credit Plans live under <strong>Enterprise</strong> and are <strong>only accessible from the management tenant</strong>. On any other tenant the menu item is hidden and the route redirects to <em>Access Denied</em>.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/55c8bbca-bfe9-4250-ba22-3a810b523364.png" alt="Credit Plans list" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Credit Plans — card list with credits, validity, and behavior badges</p></div>

      <section>
        <h2>Plan List</h2>
        <p>Plans are shown as cards, two per row, 4 per page. Each card summarizes one plan:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Plan Name &amp; Description</strong></td><td>The plan's display name and a short summary of what it grants.</td></tr>
            <tr><td><strong>Credits</strong></td><td>Number of credits the plan allots.</td></tr>
            <tr><td><strong>Validity</strong></td><td>How many days the credits stay valid.</td></tr>
            <tr><td><strong>Auto-renew badge</strong></td><td>Shows whether credits are re-granted when the validity period ends (on / off).</td></tr>
            <tr><td><strong>Rollover badge</strong></td><td>Shows whether leftover credits carry into the next period (on / off).</td></tr>
            <tr><td><strong>Updated date</strong></td><td>When the plan was last changed.</td></tr>
            <tr><td><strong>Edit / Delete</strong></td><td>Pencil opens the edit form; trash soft-deletes the plan after a confirmation.</td></tr>
          </tbody>
        </table>
        <p>A debounced <strong>search</strong> box filters by name or description (server-side). It stays visible even on a zero-result search so you can clear it.</p>
      </section>

      <section>
        <h2>Create / Edit a Plan</h2>
        <p>The <strong>Create Plan</strong> button (and the Edit pencil) opens a form with three groups. All fields are required.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/4ef415ca-c836-49bc-a837-34f6250eabe7.png" alt="Create Credit Plan form" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Create Credit Plan form</p></div>

        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Plan Name</strong></td><td>Display name, e.g. "Standard Plan".</td></tr>
            <tr><td><strong>Plan Key</strong></td><td>Stable identifier, e.g. <code>standard_plan</code>. Set once on create and <strong>not editable afterwards</strong> — it appears read-only (greyed out) on the edit form.</td></tr>
            <tr><td><strong>Description</strong></td><td>What the plan grants.</td></tr>
            <tr><td><strong>Credits</strong></td><td>Credit allotment (number, ≥ 0).</td></tr>
            <tr><td><strong>Validity (days)</strong></td><td>How long the credits last (whole number, ≥ 1).</td></tr>
            <tr><td><strong>Auto-renew</strong></td><td>Re-grant credits and restart the period when validity ends.</td></tr>
            <tr><td><strong>Rollover unused credits</strong></td><td>Carry any leftover credits into the next period.</td></tr>
          </tbody>
        </table>
        <p>Saving returns you to the list with a success toast. On edit, the plan key is never sent — it's immutable.</p>
      </section>

      <section>
        <h2>Deleting a Plan</h2>
        <p>The trash icon opens a confirmation dialog. Deleting is a <strong>soft delete</strong> — the plan is no longer listed or selectable, but the record is retained rather than permanently removed.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Set up a standard monthly plan</div>
            <div class="sz-use-case-flow">
              Click <strong>Create Plan</strong>, name it "Standard", give it a key like <code>standard_plan</code>, set the credits and a 30-day validity, and turn on <strong>Auto-renew</strong> so credits refresh each period. Save.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Offer a plan that doesn't waste credits</div>
            <div class="sz-use-case-flow">
              Create a plan with <strong>Rollover unused credits</strong> enabled so any credits left at the end of a period carry forward instead of being lost.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Choose the plan key carefully — it's permanent. The name and description can be edited anytime, but the key is the stable reference and can't be changed once the plan is created.</p>
      </div>

    </div>`,
  },

  "knowledge-base": {
    title: "Knowledge Store",
    subtitle: "Document Management",
    color: "slate",
    badge: "Knowledge Store",
    description:
      "Create document stores to organize and manage files. Upload documents (PDF, DOCX, images, video, audio), track AI processing status, mark example documents, and search content with AI-powered natural language queries.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Knowledge Store</strong> lets you create document stores, upload files, and use AI-powered search across your document content. Documents are AI-processed for content extraction and can be marked as examples for course generation.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/6a10841e-174a-48f1-923d-b13076f7d56c.png" alt="Knowledge Store" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Knowledge store management</p></div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/aad9cd87-eb16-40ec-a1fe-1b0fb15e6e36.png" alt="AI Document Search" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI-powered document search</p></div>

      <section>
        <h2>Document Stores List</h2>

        <h3>Stats Cards</h3>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Stores</strong></td><td>Total number of document stores.</td></tr>
            <tr><td><strong>Total Documents</strong></td><td>Total documents across all stores.</td></tr>
            <tr><td><strong>AI Processed</strong></td><td>Documents successfully processed by AI (with percentage).</td></tr>
            <tr><td><strong>Processing</strong></td><td>Documents currently being processed (animated pulse indicator).</td></tr>
            <tr><td><strong>Failed</strong></td><td>Documents that failed AI processing.</td></tr>
          </tbody>
        </table>

        <h3>Store Cards</h3>
        <p>Stores are displayed as a card grid. Each card shows:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Name &amp; Description</strong></td><td>Store name and description.</td></tr>
            <tr><td><strong>Stats</strong></td><td>Documents count, Processed count, and Total Size (KB/MB/GB).</td></tr>
            <tr><td><strong>Last Activity</strong></td><td>Timestamp of the most recent action.</td></tr>
            <tr><td><strong>Actions</strong></td><td>Edit, Delete, and View (opens the store detail page).</td></tr>
          </tbody>
        </table>

        <p>Search stores by name. Paginated with 6, 12, 24, or 48 items per page.</p>

        <h3>Create / Edit Store Modal</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Knowledge Store Name</strong></td><td>Required text field.</td></tr>
            <tr><td><strong>Description</strong></td><td>Required textarea with <strong>Enhance with AI</strong> button to auto-improve the description.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Store Detail Page</h2>

        <h3>Stats Cards</h3>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Documents</strong></td><td>Total documents in this store.</td></tr>
            <tr><td><strong>AI Processed</strong></td><td>Successfully processed documents with percentage.</td></tr>
            <tr><td><strong>Processing</strong></td><td>Currently processing with animated indicator.</td></tr>
            <tr><td><strong>Example Docs</strong></td><td>Documents flagged as examples for AI course generation.</td></tr>
          </tbody>
        </table>
        <p>A <strong>Processing Progress Bar</strong> shows the visual breakdown of completed vs processing vs failed documents.</p>

        <h3>AI-Powered Document Search</h3>
        <p>A prominent card with a <strong>Brain</strong> icon offers natural language search across document content. Click <strong>Open Search</strong> to launch the AI search modal and query your documents using natural language.</p>

        <h3>Upload Area</h3>
        <p>Drag-and-drop zone or file browser for uploading documents. Supported file types:</p>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Formats</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Documents</strong></td><td>PDF, DOCX, DOC, TXT, CSV</td></tr>
            <tr><td><strong>Images</strong></td><td>JPG, PNG, GIF, WEBP</td></tr>
            <tr><td><strong>Video</strong></td><td>MP4, AVI</td></tr>
            <tr><td><strong>Audio</strong></td><td>MP3, WAV, M4A</td></tr>
          </tbody>
        </table>
        <p>Maximum file size: 10 MB. Multi-file upload with individual progress bars.</p>

        <h3>Document Display</h3>
        <p>Toggle between <strong>Card View</strong> (grid) and <strong>List View</strong> (table). Each document shows:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>File Icon &amp; Name</strong></td><td>Type-specific icon with document name and file size.</td></tr>
            <tr><td><strong>Status</strong></td><td>Processing status with animated pulse if in progress.</td></tr>
            <tr><td><strong>Use as Example</strong></td><td>Toggle checkbox to mark documents as examples for AI course generation. Only available for fully processed documents.</td></tr>
            <tr><td><strong>Uploaded By</strong></td><td>Uploader name and timestamp.</td></tr>
          </tbody>
        </table>

        <h3>Filters &amp; Sort</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Search documents by name.</td></tr>
            <tr><td><strong>Status</strong></td><td>All Status, Completed, Processing, Error.</td></tr>
            <tr><td><strong>Sort</strong></td><td>Sort by Date, Name, or Status.</td></tr>
          </tbody>
        </table>

        <h3>Document Actions</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Preview</strong></td><td>Opens in-browser preview for supported types (PDF, TXT, images, audio, video).</td></tr>
            <tr><td><strong>Download</strong></td><td>Download with original filename preserved.</td></tr>
            <tr><td><strong>Delete</strong></td><td>Remove document with confirmation.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Ground AI course generation in your documents</div>
            <div class="sz-use-case-flow">
              Create a store for your department's training materials. Upload reference documents and wait for AI processing. Mark key documents as examples. When generating courses with AI, select this Knowledge Store to ground content in your own materials.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">AI-powered document search</div>
            <div class="sz-use-case-flow">
              Upload your technical documentation, policies, or reference guides. Once AI processing completes, use the AI Document Search to query your documents with natural language questions and find relevant information instantly.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Wait for AI processing to complete before using documents as examples or searching. The <strong>Processing Progress Bar</strong> shows real-time status. Use the <strong>Enhance with AI</strong> button when creating stores to generate better descriptions.</p>
      </div>

    </div>`,
  },
};
